<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ToolsDropdown from 'dashboard/components-next/captain/assistant/ToolsDropdown.vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAccountFeatures } from 'dashboard/composables/useAccountFeatures';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectTool']);

const { t } = useI18n();
const tools = useMapGetter('pilotTools/getRecords');
const { hasMarketingCloudTenantId } = useAccountFeatures();

const selectedIndex = ref(0);

// 获取工具的翻译标题
const getToolTitle = tool => {
  const key = `PILOT.ASSISTANTS.TOOLS.LIST.${tool.id?.toUpperCase()}`;
  const translated = t(key);
  // 如果翻译不存在，返回原始标题
  return translated === key ? tool.title : translated;
};

// 获取工具的翻译描述
const getToolDescription = tool => {
  const key = `PILOT.ASSISTANTS.TOOLS.LIST.${tool.id?.toUpperCase()}_DESC`;
  const translated = t(key);
  // 如果翻译不存在，返回原始描述
  return translated === key ? tool.description : translated;
};

// 根据 MIP 状态过滤工具
const filterByMipStatus = toolsList => {
  const isMipEnabled = hasMarketingCloudTenantId.value;

  return toolsList.filter(tool => {
    // 绑定 MIP：隐藏"更新联系人"，只显示"更新线索到MIP"
    if (isMipEnabled && tool.id === 'update_contact') {
      return false;
    }
    // 未绑定 MIP：隐藏"更新线索到MIP"，只显示"更新联系人"
    if (!isMipEnabled && tool.id === 'mip/leads/update_leads') {
      return false;
    }
    return true;
  });
};

const filteredTools = computed(() => {
  const search = props.searchKey?.trim().toLowerCase() || '';
  const toolsList = tools.value || [];

  // 先根据 MIP 状态过滤，再过滤并添加翻译后的标题和描述
  return filterByMipStatus(toolsList)
    .map(tool => ({
      ...tool,
      translatedTitle: getToolTitle(tool),
      translatedDescription: getToolDescription(tool),
    }))
    .filter(tool => {
      const title = tool.translatedTitle?.toLowerCase() || '';
      const description = tool.translatedDescription?.toLowerCase() || '';
      const originalTitle = tool.title?.toLowerCase() || '';
      const originalDesc = tool.description?.toLowerCase() || '';
      // 同时搜索翻译和原始内容
      return (
        title.includes(search) ||
        description.includes(search) ||
        originalTitle.includes(search) ||
        originalDesc.includes(search)
      );
    });
});

const adjustScroll = () => {};

const onSelect = idx => {
  if (idx) selectedIndex.value = idx;
  const selectedTool = filteredTools.value[selectedIndex.value];
  if (selectedTool) {
    // 返回工具对象，与Captain的TagTools保持一致
    emit('selectTool', {
      id: selectedTool.id,
      title: selectedTool.translatedTitle,
    });
  }
};

useKeyboardNavigableList({
  items: filteredTools,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(filteredTools, newListOfTools => {
  if (newListOfTools.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});
</script>

<template>
  <ToolsDropdown
    v-if="filteredTools.length"
    :items="
      filteredTools.map(t => ({
        ...t,
        title: t.translatedTitle,
        description: t.translatedDescription,
      }))
    "
    :selected-index="selectedIndex"
    class="bottom-20"
    @select="onSelect"
  />
  <template v-else />
</template>
